import { FormEvent, Fragment, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { imageUrl } from '../lib/format'
import { useSettings } from '../lib/settings'
import { Layout } from '../ui/Layout'
import { RandomButton } from '../ui/RandomButton'
import { Alert, EmptyState, Loading, Sk } from '../ui/states'

function useTitle(title: string) {
  useEffect(() => {
    document.title = `${title} · What a comical life`
  }, [title])
}

/** The first image under the heading; the rest between paragraphs, in order */
export function AboutPage() {
  const { settings } = useSettings()
  const { heading, text, images } = settings.about
  useTitle('About')
  const paragraphs = text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
  const img = (k: number) =>
    images[k] && <img key={`img-${k}`} className="about-img" src={imageUrl(images[k].key)} alt={images[k].altText} />
  const extra = images.slice(paragraphs.length + 1).map((_, k) => img(paragraphs.length + 1 + k))
  return (
    <Layout>
      <div className="column stack">
        <header className="vstack gap-sm">
          <div className="eyebrow">About</div>
          <h1 className="h1">{heading || 'About'}</h1>
        </header>
        {img(0)}
        {paragraphs.length === 0 && images.length === 0 && <p className="lead">The artist hasn't written this page yet.</p>}
        {paragraphs.map((p, k) => (
          <Fragment key={k}>
            <p className="body-text">{p}</p>
            {img(k + 1)}
          </Fragment>
        ))}
        {extra}
        <div className="btns mt-sm">
          <Link className="btn solid" to="/contact">
            Get in touch
          </Link>
          <Link className="btn" to="/">
            Read the comics
          </Link>
        </div>
      </div>
    </Layout>
  )
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function ContactPage() {
  useTitle('Contact')
  const empty = { name: '', email: '', message: '', website: '' }
  const [form, setForm] = useState(empty)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle')
  const [failure, setFailure] = useState('')
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value })

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const errs: Record<string, string> = {}
    if (!form.name.trim()) errs.name = 'Enter your name.'
    if (!EMAIL.test(form.email.trim())) errs.email = form.email.trim() ? 'Enter a valid email address, like name@example.com.' : 'Enter your email address.'
    if (!form.message.trim()) errs.message = 'Write a message before sending.'
    setErrors(errs)
    if (Object.keys(errs).length) return
    setState('busy')
    try {
      await api.post('/api/contact', form)
      setState('sent')
      setForm(empty)
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fields).length) {
        setErrors(error.fields)
        setState('idle')
        return
      }
      setFailure(error instanceof ApiError && error.status >= 400 && error.status !== 500 ? error.message : 'Your text is still in the form. Check your connection and try again.')
      setState('failed')
    }
  }

  const input = (id: string, key: 'name' | 'email' | 'message', label: string) => (
    <div className="formfield">
      <label className="lbl" htmlFor={id}>
        {label}
      </label>
      {key === 'message' ? (
        <textarea id={id} className={errors[key] ? 'field medium err' : 'field medium'} value={form[key]} onChange={set(key)} disabled={state === 'busy'} aria-invalid={Boolean(errors[key])} aria-describedby={errors[key] ? `${id}-err` : undefined} />
      ) : (
        <input id={id} className={errors[key] ? 'field err' : 'field'} type={key === 'email' ? 'email' : 'text'} autoComplete={key} value={form[key]} onChange={set(key)} disabled={state === 'busy'} aria-invalid={Boolean(errors[key])} aria-describedby={errors[key] ? `${id}-err` : undefined} />
      )}
      {errors[key] && (
        <div id={`${id}-err`} className="ferr" role="alert">
          {errors[key]}
        </div>
      )}
    </div>
  )

  return (
    <Layout>
      <div className="column stack">
        <div className="vstack gap-sm">
          <h1 className="h1">Contact</h1>
          <p className="lead md">
            Questions, collaborations, or just want to say hi? Send a message and I'll reply by email.
          </p>
        </div>
        {state === 'sent' ? (
          <Alert tone="ok" title="Message sent." actions={<button type="button" className="btn sm" onClick={() => setState('idle')}>Send another message</button>}>
            Thanks for writing. I'll reply to the email address you gave.
          </Alert>
        ) : (
          <form className="form" onSubmit={submit} noValidate>
            {state === 'failed' && <Alert title="Your message couldn't be sent.">{failure}</Alert>}
            {input('ct-name', 'name', 'Name')}
            {input('ct-email', 'email', 'Email')}
            {input('ct-msg', 'message', 'Message')}
            <input className="vh" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={form.website} onChange={set('website')} />
            <div className="hstack gap-md">
              <button type="submit" className="btn solid" disabled={state === 'busy'}>
                {state === 'busy' && <span className="spin" aria-hidden="true" />}
                {state === 'busy' ? 'Sending' : state === 'failed' ? 'Try again' : 'Send message'}
              </button>
              <span className="hint">Your email is only used to reply.</span>
            </div>
          </form>
        )}
      </div>
    </Layout>
  )
}

/** Opened from the link in a confirmation or newsletter email; acts once, on load */
function TokenPage({ action }: { action: 'confirm' | 'unsubscribe' }) {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [state, setState] = useState<'busy' | 'done' | 'failed'>('busy')
  const [failure, setFailure] = useState('')
  const started = useRef(false)
  useTitle(action === 'confirm' ? 'Confirm newsletter' : 'Unsubscribe')

  useEffect(() => {
    if (started.current) return
    started.current = true
    api
      .post(`/api/newsletter/${action}`, { token })
      .then(() => setState('done'))
      .catch((error) => {
        setFailure(error instanceof ApiError && error.status === 404 ? error.message : 'Check your connection and try again.')
        setState('failed')
      })
  }, [action, token])

  let body
  if (state === 'busy') {
    body = (
      <Loading label={action === 'confirm' ? 'Confirming' : 'Unsubscribing'}>
        <Sk h={120} />
      </Loading>
    )
  } else if (state === 'failed') {
    body = (
      <EmptyState error title={action === 'confirm' ? "We couldn't confirm your sign-up" : "We couldn't unsubscribe you"} actions={<div className="btns"><Link className="btn solid" to="/">Go to home</Link></div>}>
        {failure}
      </EmptyState>
    )
  } else if (action === 'confirm') {
    body = (
      <EmptyState icon="mail" title="You're subscribed" actions={<div className="btns"><Link className="btn solid" to="/">Read the latest comics</Link></div>}>
        Thanks for confirming. New newsletters will arrive in your inbox.
      </EmptyState>
    )
  } else {
    body = (
      <EmptyState icon="mail" title="You're unsubscribed" actions={<div className="btns"><Link className="btn solid" to="/">Go to home</Link></div>}>
        You won't get the newsletter any more. You can sign up again at the bottom of any page.
      </EmptyState>
    )
  }
  return (
    <Layout>
      <div className="column">{body}</div>
    </Layout>
  )
}

export const NewsletterConfirmPage = () => <TokenPage action="confirm" />
export const UnsubscribePage = () => <TokenPage action="unsubscribe" />

export function NotFoundPage() {
  useTitle('Page not found')
  return (
    <Layout>
      <div className="column">
        <EmptyState
          title="We couldn't find that page"
          actions={
            <div className="btns">
              <Link className="btn solid" to="/">
                Go to home
              </Link>
              <RandomButton label="Random comic" />
            </div>
          }
        >
          The link may be old or mistyped.
        </EmptyState>
      </div>
    </Layout>
  )
}
